/**
 * V198.4: Single-Source-of-Truth Trust Score Calculation
 * 
 * FORMULA: TrustScore = 10 × firstDegree + 1 × secondDegree + (isVerified ? 100 : 0)
 * 
 * NO BASE POINTS - This is the canonical implementation used everywhere:
 * - Profile cards
 * - Network stats
 * - Database sync
 * - API responses
 */

export interface TrustScoreInput {
  /** Number of 1st-degree connections (friends) */
  firstDegreeCount: number;
  /** Number of 2nd-degree connections (friends of friends) */
  secondDegreeCount: number;
  /** Whether user is verified */
  isVerified: boolean;
}

export interface TrustScoreBreakdown {
  /** Total trust score */
  total: number;
  /** Points from 1st-degree connections (firstDegree × 10) */
  firstDegreePoints: number;
  /** Points from 2nd-degree connections (secondDegree × 1) */
  secondDegreePoints: number;
  /** Points from verification (100 if verified, 0 otherwise) */
  verificationBonus: number;
}

/**
 * Calculate trust score using the canonical formula.
 * 
 * @param opts - Trust score input parameters
 * @returns Total trust score
 * 
 * @example
 * // Rani: 3 friends, 2 second-degree, verified
 * calculateTrustScore({
 *   firstDegreeCount: 3,
 *   secondDegreeCount: 2,
 *   isVerified: true
 * }); // Returns 132 (30 + 2 + 100)
 * 
 * @example
 * // Alice: 1 friend, 1 second-degree, not verified
 * calculateTrustScore({
 *   firstDegreeCount: 1,
 *   secondDegreeCount: 1,
 *   isVerified: false
 * }); // Returns 11 (10 + 1 + 0)
 */
export function calculateTrustScore(opts: TrustScoreInput): number {
  const first = Math.max(0, opts.firstDegreeCount || 0);
  const second = Math.max(0, opts.secondDegreeCount || 0);

  return first * 10 + second + (opts.isVerified ? 100 : 0);
}

/**
 * Calculate trust score with detailed breakdown.
 * 
 * @param opts - Trust score input parameters
 * @returns Detailed breakdown of trust score components
 * 
 * @example
 * const breakdown = calculateTrustScoreBreakdown({
 *   firstDegreeCount: 3,
 *   secondDegreeCount: 2,
 *   isVerified: true
 * });
 * // {
 * //   total: 132,
 * //   firstDegreePoints: 30,
 * //   secondDegreePoints: 2,
 * //   verificationBonus: 100
 * // }
 */
export function calculateTrustScoreBreakdown(opts: TrustScoreInput): TrustScoreBreakdown {
  const first = Math.max(0, opts.firstDegreeCount || 0);
  const second = Math.max(0, opts.secondDegreeCount || 0);

  const firstDegreePoints = first * 10;
  const secondDegreePoints = second * 1;
  const verificationBonus = opts.isVerified ? 100 : 0;

  return {
    total: firstDegreePoints + secondDegreePoints + verificationBonus,
    firstDegreePoints,
    secondDegreePoints,
    verificationBonus,
  };
}

/**
 * Calculate trust score from database user record.
 * 
 * @param user - User record from database
 * @returns Total trust score
 * 
 * @example
 * const user = await supabase.from('users').select('*').eq('id', userId).single();
 * const trustScore = calculateTrustScoreFromUser(user.data);
 */
export function calculateTrustScoreFromUser(user: {
  friends_count?: number | null;
  connections_count?: number | null;
  is_verified?: boolean | null;
}): number {
  const firstDegree = user.friends_count ?? 0;
  const totalConnections = user.connections_count ?? 0;
  const secondDegree = Math.max(totalConnections - firstDegree, 0);

  return calculateTrustScore({
    firstDegreeCount: firstDegree,
    secondDegreeCount: secondDegree,
    isVerified: user.is_verified ?? false,
  });
}

/**
 * SQL formula for calculating trust score in database queries.
 * Use this in UPDATE statements or computed columns.
 * 
 * @example
 * ```sql
 * UPDATE public.users
 * SET trust_score = (friends_count * 10)
 *                 + GREATEST(connections_count - friends_count, 0)
 *                 + CASE WHEN is_verified THEN 100 ELSE 0 END;
 * ```
 */
export const TRUST_SCORE_SQL_FORMULA = `
  (friends_count * 10)
  + GREATEST(connections_count - friends_count, 0)
  + CASE WHEN is_verified THEN 100 ELSE 0 END
`.trim();

